import { AudioLines, FileText, Layers, MessageSquare } from 'lucide-react';
import type { ApprovalStatus, AssetType } from '@/lib/api/types';
import { APPROVAL_TONE } from '@/components/Misc';
import { msToClock } from '@/lib/media/timecode';
import { cx } from '@/lib/format';

export function TypeBadge({ type, durationMs, pageCount, timeRef }: { type: AssetType; durationMs?: number | null; pageCount?: number | null; timeRef?: React.Ref<HTMLSpanElement> }) {
  if (type === 'video' || (type === 'audio' && durationMs)) {
    return (
      <span className="pointer-events-none absolute bottom-1.5 left-1.5 flex items-center gap-1 rounded bg-black/65 px-1.5 py-0.5 font-mono text-xs text-white tabular-nums">
        {type === 'audio' && <AudioLines size={11} />}
        <span ref={timeRef}>{msToClock(durationMs ?? 0)}</span>
      </span>
    );
  }
  if (type === 'audio') return <span className="pointer-events-none absolute bottom-1.5 left-1.5 rounded bg-black/65 p-1 text-white"><AudioLines size={12} /></span>;
  if (type === 'document') {
    return (
      <span className="pointer-events-none absolute bottom-1.5 left-1.5 flex items-center gap-1 rounded bg-black/65 px-1.5 py-0.5 text-xs text-white">
        <FileText size={11} /> {pageCount ?? ''}
      </span>
    );
  }
  return null;
}

export function ApprovalDot({ status, className }: { status: ApprovalStatus; className?: string }) {
  if (status === 'none') return null;
  const t = APPROVAL_TONE[status];
  return <span title={t.label} aria-label={t.label} className={cx('pointer-events-none absolute top-1.5 right-1.5 h-2.5 w-2.5 rounded-full ring-2 ring-black/40', t.dot, className)} />;
}

export function CountBadges({ versions, comments, unresolved }: { versions: number; comments: number; unresolved?: number }) {
  if (versions <= 1 && comments === 0) return null;
  return (
    <span className="pointer-events-none absolute right-1.5 bottom-1.5 flex items-center gap-1 text-xs text-white">
      {versions > 1 && (
        <span className="flex items-center gap-0.5 rounded bg-black/65 px-1 py-0.5" title={`${versions} versions`}>
          <Layers size={11} /> v{versions}
        </span>
      )}
      {comments > 0 && (
        <span className={cx('flex items-center gap-0.5 rounded px-1 py-0.5', unresolved ? 'bg-warn text-black' : 'bg-black/65')} title={`${comments} comments${unresolved ? `, ${unresolved} unresolved` : ''}`}>
          <MessageSquare size={11} /> {comments}
        </span>
      )}
    </span>
  );
}

export function ProgressRing({ progress, label, size = 36 }: { progress: number | null; label?: string; size?: number }) {
  const r = (size - 6) / 2;
  const c = 2 * Math.PI * r;
  const p = progress == null ? null : Math.max(0, Math.min(1, progress));
  return (
    <div className="pointer-events-none absolute inset-0 flex flex-col items-center justify-center gap-1 bg-black/30 text-white" role="progressbar" aria-valuenow={p == null ? undefined : Math.round(p * 100)} aria-valuemin={0} aria-valuemax={100} aria-label={label}>
      <svg width={size} height={size} className={p == null ? 'animate-spin' : ''}>
        <circle cx={size / 2} cy={size / 2} r={r} stroke="rgba(255,255,255,0.3)" strokeWidth={3} fill="none" />
        <circle cx={size / 2} cy={size / 2} r={r} stroke="white" strokeWidth={3} fill="none" strokeLinecap="round" strokeDasharray={c} strokeDashoffset={p == null ? c * 0.75 : c * (1 - p)} transform={`rotate(-90 ${size / 2} ${size / 2})`} />
      </svg>
      {label && <span className="text-xs font-medium drop-shadow">{label}</span>}
    </div>
  );
}

// --- release2/B --- G3: a required field has no value yet (`AssetCard.incomplete`)
export function IncompleteCorner({ incomplete }: { incomplete?: boolean }) {
  if (!incomplete) return null;
  return (
    <span
      title="Required fields are missing"
      aria-label="Required fields are missing"
      data-incomplete
      className="pointer-events-none absolute top-0 right-0 h-5 w-5 bg-warn"
      style={{ clipPath: 'polygon(0 0, 100% 0, 100% 100%)' }}
    />
  );
}

// --- release2/C ---
import { ShieldAlert } from 'lucide-react';
import type { RightsStatus } from '@/features/rights/status';
import { isEmbargoed } from '@/features/rights/status';
import { CalendarClock } from 'lucide-react';
/** C4: usage rights expiring (within 30 days) or expired; nothing for `none` / `valid`. release4/R: "Embargoed" while `valid_from` is in the future (wins). */
export function RightsBadge({ status, expiresAt, validFrom }: { status?: RightsStatus | null; expiresAt?: string | null; validFrom?: string | null }) {
  {/* release4/R */}
  if (isEmbargoed(validFrom)) {
    const label = 'Embargoed';
    return (
      <span title={`${label} until ${new Date(validFrom as string).toLocaleDateString()}`} aria-label={label} data-rights-badge="embargoed" className="pointer-events-none absolute top-1 right-5 flex items-center gap-0.5 rounded bg-fg px-1 py-0.5 text-[10px] font-medium leading-none text-bg">
        <CalendarClock size={10} /> {label}
      </span>
    );
  }
  if (status !== 'expiring' && status !== 'expired') return null;
  const label = status === 'expired' ? 'Rights expired' : 'Rights expiring';
  return (
    <span title={expiresAt ? `${label} · ${new Date(expiresAt).toLocaleDateString()}` : label} aria-label={label} data-rights-badge={status} className={cx('pointer-events-none absolute top-1 right-5 flex items-center gap-0.5 rounded px-1 py-0.5 text-[10px] font-medium leading-none', status === 'expired' ? 'bg-danger text-white' : 'bg-warn text-black')}>
      <ShieldAlert size={10} /> {status === 'expired' ? 'Expired' : 'Expiring'}
    </span>
  );
}

// --- release3/B --- DEL-1: internal-only assets (`AssetCard.share_excluded`) never appear through share links
import { EyeOff } from 'lucide-react';
export function InternalOnlyBadge({ excluded }: { excluded?: boolean }) {
  if (!excluded) return null;
  return (
    <span title="Internal only — hidden from share links" aria-label="Internal only" data-share-excluded-badge className="pointer-events-none absolute top-1.5 left-1.5 flex items-center gap-0.5 rounded bg-black/65 px-1 py-0.5 text-[10px] font-medium leading-none text-white">
      <EyeOff size={10} /> Internal
    </span>
  );
}
